// SubTasksConfig.tsx (updated with checkboxes)
import React, { useState, useEffect } from 'react';
import { useConfigStore } from '@/store/configStore';
import { Edit2, Trash2 } from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Button } from '@/components/common/Button';

export const SubTasksConfig: React.FC<{ onSetActionCallback?: (callbacks: { add?: () => void }) => void }> = ({ onSetActionCallback }) => {
  const { 
    taskTemplates, 
    fetchTaskTemplates, 
    updateTaskTemplate 
  } = useConfigStore();
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [newSubTask, setNewSubTask] = useState('');
  const [editingSubTask, setEditingSubTask] = useState<{ templateId: string; subTask: string; index: number } | null>(null);
  const [selectedSubTasks, setSelectedSubTasks] = useState<string[]>([]);

  useEffect(() => {
    fetchTaskTemplates();
  }, []);

  useEffect(() => {
    onSetActionCallback?.({
      add: () => setShowAddModal(true)
    });
  }, [onSetActionCallback]);

  // Flatten all subtasks with their parent task info
  const allSubTasks = taskTemplates.reduce((acc, template) => {
    const subTasksWithParent = (template.subTasks || []).map((subTask, index) => ({
      id: `${template._id}-${index}`,
      templateId: template._id,
      templateName: template.name,
      subTask: subTask,
      index: index
    }));
    return [...acc, ...subTasksWithParent];
  }, [] as any[]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedSubTasks(allSubTasks.map(st => st.id));
    } else {
      setSelectedSubTasks([]);
    }
  };

  const handleSelectSubTask = (subTaskId: string) => {
    setSelectedSubTasks(prev => 
      prev.includes(subTaskId) 
        ? prev.filter(id => id !== subTaskId)
        : [...prev, subTaskId]
    );
  };

  const handleAddSubTask = async () => {
    if (selectedTask && newSubTask) {
      const template = taskTemplates.find(t => t._id === selectedTask);
      if (template) {
        const updatedSubTasks = [...(template.subTasks || []), newSubTask];
        await updateTaskTemplate(template._id, { 
          ...template,
          subTasks: updatedSubTasks 
        });
        setNewSubTask('');
        setSelectedTask('');
        setShowAddModal(false);
                await fetchTaskTemplates();
      }
    }
  };

  const handleDeleteSubTask = async (templateId: string, index: number) => {
    const template = taskTemplates.find(t => t._id === templateId);
    if (template && window.confirm('Are you sure you want to delete this sub-task?')) {
      const updatedSubTasks = template.subTasks.filter((_, i) => i !== index);
      await updateTaskTemplate(template._id, { 
        ...template,
        subTasks: updatedSubTasks 
      });
      await fetchTaskTemplates();
    }
  };

  const handleUpdateSubTask = async () => {
    if (editingSubTask && newSubTask) {
      const template = taskTemplates.find(t => t._id === editingSubTask.templateId);
      if (template) {
        const updatedSubTasks = [...template.subTasks];
        updatedSubTasks[editingSubTask.index] = newSubTask;
        await updateTaskTemplate(template._id, { 
          ...template,
          subTasks: updatedSubTasks 
        });
        setEditingSubTask(null);
        setNewSubTask('');
        setShowAddModal(false);
        await fetchTaskTemplates();
      }
    }
  };

  return (
    <div className="h-full bg-white mx-0 my-0">
      <div className="h-full flex flex-col">
        <div className="overflow-auto flex-1">
          <table className="w-full border border-gray-200 border-collapse">
            <thead>
              <tr className="border border-gray-200">
                <th className="text-left bg-gray-100 px-4 border py-1 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-gray-300"
                    onChange={handleSelectAll}
                    checked={selectedSubTasks.length === allSubTasks.length && allSubTasks.length > 0}
                  />
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  SUB-TASK
                </th>
                <th className="text-left bg-gray-100 px-4 border py-1 text-xs font-medium text-gray-600 uppercase tracking-wider">
                  TASK
                </th>
                <th className="w-20 bg-gray-100 border"></th>
              </tr>
            </thead>
            <tbody>
              {allSubTasks.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-8 text-gray-500 border">
                    No sub-tasks found
                  </td>
                </tr>
              ) : (
                allSubTasks.map((item) => (
                  <tr key={item.id} className="border border-gray-100 hover:bg-gray-50">
                    <td className="px-4 border py-1">
                      <input 
                        type="checkbox" 
                        className="rounded border-gray-300"
                        checked={selectedSubTasks.includes(item.id)}
                        onChange={() => handleSelectSubTask(item.id)}
                      />
                    </td>
                    <td className="px-4 border py-1 text-sm font-medium text-gray-900">
                      {item.subTask}
                    </td>
                    <td className="px-4 border py-1 text-sm text-gray-600">
                      {item.templateName}
                    </td>
                    <td className="px-4 border py-1">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          onClick={() => {
                            setEditingSubTask(item);
                            setNewSubTask(item.subTask);
                            setShowAddModal(true);
                          }}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          onClick={() => handleDeleteSubTask(item.templateId, item.index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 border py-3 border-t border-gray-200 bg-gray-50 flex items-center">
          <div className="text-sm text-gray-600 uppercase">
            COUNT <span className="ml-4 font-medium">{allSubTasks.length}</span>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingSubTask(null);
          setNewSubTask('');
          setSelectedTask('');
        }}
        title={editingSubTask ? 'Edit Sub-task' : 'Add Sub-task'}
        size="md"
      >
        <div className="space-y-4">
          {!editingSubTask && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Task
              </label>
              <Select
                value={selectedTask}
                onChange={(e) => setSelectedTask(e.target.value)}
              >
                <option value="">Select a task</option>
                {taskTemplates.map(template => (
                  <option key={template._id} value={template._id}>
                    {template.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {editingSubTask && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task
              </label>
              <Input
                value={editingSubTask.templateName}
                disabled
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sub-task Name
            </label>
            <Input
              value={newSubTask}
              onChange={(e) => setNewSubTask(e.target.value)}
              placeholder="Enter sub-task name"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowAddModal(false);
                setEditingSubTask(null);
                setNewSubTask('');
                setSelectedTask('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={editingSubTask ? handleUpdateSubTask : handleAddSubTask}
              disabled={!newSubTask || (!editingSubTask && !selectedTask)}
            >
              {editingSubTask ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};